import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Send,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  RefreshCw
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useAuth } from '@/features/auth';
import { useContactForm, useContactCategories, useUserProfile, generateMathCaptcha } from '../hooks/useContactForm';

export function ContactPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const { submit, isSubmitting, isSuccess, error, reset } = useContactForm();
  const { data: categories } = useContactCategories();
  const { t, i18n } = useTranslation('help');

  const isAuthenticated = !!user;
  const isLoadingData = authLoading || (isAuthenticated && profileLoading);
  const captchaLocale = (i18n.language === 'en' ? 'en' : 'de') as 'de' | 'en';

  const [categoryId, setCategoryId] = useState<string>('');
  const [message, setMessage] = useState('');
  const [captcha, setCaptcha] = useState(() => generateMathCaptcha(captchaLocale));
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  // Guest user fields
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  const refreshCaptcha = () => {
    setCaptcha(generateMathCaptcha(captchaLocale));
    setCaptchaAnswer('');
    setCaptchaError(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate captcha
    if (parseInt(captchaAnswer, 10) !== captcha.answer) {
      setCaptchaError(true);
      return;
    }

    try {
      await submit({
        category_id: categoryId || null,
        message: message.trim(),
        first_name: isAuthenticated ? (profile?.first_name || '') : guestFirstName,
        last_name: isAuthenticated ? (profile?.last_name || '') : guestLastName,
        guest_email: !isAuthenticated ? guestEmail : undefined,
      });
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleReset = () => {
    reset();
    setCategoryId('');
    setMessage('');
    setGuestFirstName('');
    setGuestLastName('');
    setGuestEmail('');
    refreshCaptcha();
  };

  // Validation for guest users
  const isGuestFormValid = !isAuthenticated
    ? guestFirstName.trim() && guestEmail.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)
    : true;

  // Success State
  if (isSuccess) {
    return (
      <div className="min-h-full pb-8 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-white">{t('contact.title')}</h1>
          <p className="text-white/60 text-sm">{t('contact.messageSent')}</p>
        </div>

        <div className="bg-green-500/20 border border-green-500/30 rounded-[15px] p-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">
            {t('contact.successTitle')}
          </h2>
          <p className="text-white/70 mb-6">
            {isAuthenticated ? t('contact.successDescriptionAuth') : t('contact.successDescriptionGuest')}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={handleReset}
              className="w-full py-3 px-4 bg-pp-orange text-white rounded-[15px]
                         font-medium hover:bg-pp-orange/90 transition-colors"
            >
              {t('contact.sendAnother')}
            </button>
            <Link
              to="/help"
              className="w-full py-3 px-4 glass-effect border border-white/25 text-white rounded-[15px]
                         font-medium hover:bg-white/10 transition-colors text-center"
            >
              {t('contact.backToHelp')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full pb-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-white">{t('contact.formTitle')}</h1>
        <p className="text-white/60 text-sm">
          {t('contact.formSubtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* User Info - Different for authenticated vs guest */}
        {isAuthenticated ? (
          // Authenticated User Info (read-only)
          <div className="glass-effect rounded-[15px] p-4 space-y-3">
            <h3 className="text-sm font-medium text-white/50 mb-3">
              {t('contact.contactDetails')}
            </h3>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-[10px] flex items-center justify-center">
                <User className="h-5 w-5 text-white/50" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-white/40">{t('contact.name')}</label>
                <p className="text-white font-medium">
                  {isLoadingData ? (
                    <span className="bg-white/10 rounded h-5 w-32 inline-block animate-pulse" />
                  ) : (
                    `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || t('contact.notSpecified')
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-[10px] flex items-center justify-center">
                <Mail className="h-5 w-5 text-white/50" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-white/40">{t('contact.email')}</label>
                <p className="text-white font-medium">
                  {isLoadingData ? (
                    <span className="bg-white/10 rounded h-5 w-48 inline-block animate-pulse" />
                  ) : (
                    user?.email || t('contact.notSpecified')
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Guest User Info (editable)
          <div className="glass-effect rounded-[15px] p-4 space-y-4">
            <h3 className="text-sm font-medium text-white/50 mb-3">
              {t('contact.contactDetails')}
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1.5">
                  {t('contact.firstName')}
                </label>
                <input
                  type="text"
                  value={guestFirstName}
                  onChange={(e) => setGuestFirstName(e.target.value)}
                  required
                  placeholder={t('contact.firstNamePlaceholder')}
                  className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1.5">
                  {t('contact.lastName')}
                </label>
                <input
                  type="text"
                  value={guestLastName}
                  onChange={(e) => setGuestLastName(e.target.value)}
                  placeholder={t('contact.lastNamePlaceholder')}
                  className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-1.5">
                {t('contact.emailAddress')}
              </label>
              <input
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                required
                placeholder={t('contact.emailPlaceholder')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                           text-white placeholder-white/40 focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                           outline-none transition-all"
              />
              <p className="text-xs text-white/40 mt-1">
                {t('contact.emailHint')}
              </p>
            </div>
          </div>
        )}

        {/* Category Selection */}
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('contact.topic')}
          </label>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                       text-white focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                       outline-none transition-all"
          >
            <option value="" className="bg-pp-dark">{t('contact.selectPlaceholder')}</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.id} className="bg-pp-dark">
                {cat.name}
              </option>
            ))}
            <option value="" className="bg-pp-dark">{t('contact.other')}</option>
          </select>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('contact.message')}
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            minLength={10}
            rows={6}
            placeholder={t('contact.messagePlaceholder')}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                       text-white placeholder-white/40 focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                       outline-none transition-all resize-none"
          />
          <p className="text-xs text-white/40 mt-1">
            {t('contact.minChars')}
          </p>
        </div>

        {/* Math Captcha */}
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('contact.captchaLabel')}
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-white/10 rounded-[10px] px-4 py-3 flex items-center justify-between">
              <span className="font-medium text-white">
                {captcha.question}
              </span>
              <button
                type="button"
                onClick={refreshCaptcha}
                className="p-1 hover:bg-white/10 rounded-[5px] transition-colors"
                title={t('contact.newCaptcha')}
              >
                <RefreshCw className="h-4 w-4 text-white/50" />
              </button>
            </div>
            <input
              type="number"
              value={captchaAnswer}
              onChange={(e) => {
                setCaptchaAnswer(e.target.value);
                setCaptchaError(false);
              }}
              required
              placeholder="?"
              className={`w-20 px-4 py-3 rounded-[10px] text-center text-white
                         outline-none transition-all bg-white/10
                         ${captchaError
                           ? 'border-2 border-red-500 focus:border-red-500 focus:ring-red-500/20'
                           : 'border border-white/20 focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20'
                         }`}
            />
          </div>
          {captchaError && (
            <p className="text-sm text-red-400 mt-1">
              {t('contact.captchaError')}
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-[15px] p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-medium">{t('errors.sendFailed')}</p>
              <p className="text-red-200/70 text-sm">
                {error.message || t('errors.sendFailedFallback')}
              </p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || !message.trim() || message.length < 10 || !isGuestFormValid}
          className="w-full py-4 px-6 bg-pp-orange text-white rounded-[15px]
                     font-medium hover:bg-pp-orange/90 transition-colors
                     disabled:bg-white/20 disabled:text-white/40 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              {t('contact.sending')}
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              {t('contact.send')}
            </>
          )}
        </button>

        <p className="text-xs text-white/40 text-center">
          {t('contact.privacyPrefix')}{' '}
          <Link to="/legal/privacy" className="text-pp-orange hover:underline">
            {t('contact.privacyLink')}
          </Link>{' '}
          {t('contact.privacySuffix')}
        </p>
      </form>
    </div>
  );
}
